import React, { useState, useEffect } from 'react';
import { useContent } from '../contexts/ContentContext';
import '../assets/styles/Admin.scss';

function Admin() {
  const { content, updateContent } = useContent();
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('main');

  // Keyboard shortcut CTRL+SHIFT+A to toggle admin
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.code === 'KeyA') {
        event.preventDefault();
        setIsOpen((prev) => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);

  const handleSave = (newContent: any) => {
    updateContent(newContent);
  };

  const handleDeleteItem = (section: string, index: number) => {
    const newContent = { ...content };
    if (Array.isArray(newContent[section as keyof typeof content])) {
      (newContent[section as keyof typeof content] as any[]).splice(index, 1);
      handleSave(newContent);
    }
  };

  const handleAddItem = (section: string) => {
    const newContent = { ...content };
    if (Array.isArray(newContent[section as keyof typeof content])) {
      const items = newContent[section as keyof typeof content] as any[];
      items.push({
        title: 'New Item',
        description: 'Item description',
        technologies: [],
        status: 'In Progress'
      });
      handleSave(newContent);
    }
  };

  const handleUpdateItem = (section: string, index: number, field: string, value: any) => {
    const newContent = { ...content };
    if (Array.isArray(newContent[section as keyof typeof content])) {
      const items = newContent[section as keyof typeof content] as any[];
      items[index] = { ...items[index], [field]: value };
      handleSave(newContent);
    }
  };

  const handleUpdateNestedField = (section: string, field: string, value: any) => {
    const newContent = { ...content };
    if (section === 'main' || section === 'footer') {
      (newContent as any)[section] = {
        ...(newContent as any)[section],
        [field]: value
      };
      handleSave(newContent);
    }
  };

  const closeAdmin = () => setIsOpen(false);

  if (!isOpen) {
    return null; // No visible button - use CTRL+SHIFT+A to open
  }

  return (
    <div className="admin-ide">
      <div className="admin-header">
        <h1>⚙️ Content Editor IDE</h1>
        <div className="admin-header-info">
          <span className="shortcut-hint">Press CTRL+SHIFT+A to toggle</span>
          <button className="admin-close" onClick={closeAdmin} title="Close (CTRL+SHIFT+A)">✕</button>
        </div>
      </div>
      
      <div className="admin-container">
        {/* Sidebar Navigation */}
        <nav className="admin-sidebar">
          <div className="sidebar-title">Sections</div>
          <ul className="section-list">
            <li
              className={activeSection === 'main' ? 'active' : ''}
              onClick={() => setActiveSection('main')}
            >
              🏠 Main
            </li>
            <li
              className={activeSection === 'expertise' ? 'active' : ''}
              onClick={() => setActiveSection('expertise')}
            >
              💡 Expertise
            </li>
            <li
              className={activeSection === 'timeline' ? 'active' : ''}
              onClick={() => setActiveSection('timeline')}
            >
              📅 Timeline
            </li>
            <li
              className={activeSection === 'projects' ? 'active' : ''}
              onClick={() => setActiveSection('projects')}
            >
              🚀 Projects
            </li>
            <li
              className={activeSection === 'educationJourney' ? 'active' : ''}
              onClick={() => setActiveSection('educationJourney')}
            >
              🎓 Education
            </li>
            <li
              className={activeSection === 'aiWork' ? 'active' : ''}
              onClick={() => setActiveSection('aiWork')}
            >
              🤖 AI Work
            </li>
            <li
              className={activeSection === 'leadership' ? 'active' : ''}
              onClick={() => setActiveSection('leadership')}
            >
              👥 Leadership
            </li>
            <li
              className={activeSection === 'certifications' ? 'active' : ''}
              onClick={() => setActiveSection('certifications')}
            >
              🏆 Certifications
            </li>
            <li
              className={activeSection === 'footer' ? 'active' : ''}
              onClick={() => setActiveSection('footer')}
            >
              🔗 Footer
            </li>
          </ul>
        </nav>

        {/* Main Editor Area */}
        <div className="admin-editor">
          {activeSection === 'main' && (
            <div className="section-editor">
              <h2>Main Section</h2>
              <div className="form-group">
                <label>Name:</label>
                <input
                  type="text"
                  value={content.main.name}
                  onChange={(e) => handleUpdateNestedField('main', 'name', e.target.value)}
                  placeholder="Your name"
                />
              </div>
              <div className="form-group">
                <label>Title:</label>
                <input
                  type="text"
                  value={content.main.title}
                  onChange={(e) => handleUpdateNestedField('main', 'title', e.target.value)}
                  placeholder="Your title"
                />
              </div>
              <div className="form-group">
                <label>Phone:</label>
                <input
                  type="text"
                  value={content.main.phone}
                  onChange={(e) => handleUpdateNestedField('main', 'phone', e.target.value)}
                  placeholder="+1 (555) 000-0000"
                />
              </div>
              <div className="form-group">
                <label>Email:</label>
                <input
                  type="email"
                  value={content.main.email}
                  onChange={(e) => handleUpdateNestedField('main', 'email', e.target.value)}
                  placeholder="your.email@example.com"
                />
              </div>
              <div className="form-group">
                <label>Image URL:</label>
                <input
                  type="text"
                  value={content.main.image}
                  onChange={(e) => handleUpdateNestedField('main', 'image', e.target.value)}
                  placeholder="/image-path.jpg"
                />
              </div>
            </div>
          )}

          {activeSection === 'timeline' && (
            <div className="section-editor">
              <div className="section-header">
                <h2>Timeline</h2>
                <button className="btn-add" onClick={() => handleAddItem('timeline')}>+ Add Item</button>
              </div>
              <div className="items-list">
                {content.timeline?.map((item: any, index: number) => (
                  <div key={index} className="item-editor">
                    <div className="item-header">
                      <h3>Item {index + 1}</h3>
                      <button 
                        className="btn-delete" 
                        onClick={() => handleDeleteItem('timeline', index)}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                    <div className="form-group">
                      <label>Date:</label>
                      <input
                        type="text"
                        value={item.date}
                        onChange={(e) => handleUpdateItem('timeline', index, 'date', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Title:</label>
                      <input
                        type="text"
                        value={item.title}
                        onChange={(e) => handleUpdateItem('timeline', index, 'title', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Subtitle:</label>
                      <input
                        type="text"
                        value={item.subtitle}
                        onChange={(e) => handleUpdateItem('timeline', index, 'subtitle', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Description:</label>
                      <textarea
                        value={item.description}
                        onChange={(e) => handleUpdateItem('timeline', index, 'description', e.target.value)}
                        rows={3}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === 'expertise' && (
            <div className="section-editor">
              <div className="section-header">
                <h2>Expertise</h2>
                <button className="btn-add" onClick={() => handleAddItem('expertise')}>+ Add Item</button>
              </div>
              <div className="items-list">
                {content.expertise?.map((item: any, index: number) => (
                  <div key={index} className="item-editor">
                    <div className="item-header">
                      <h3>{item.title}</h3>
                      <button 
                        className="btn-delete" 
                        onClick={() => handleDeleteItem('expertise', index)}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                    <div className="form-group">
                      <label>Icon:</label>
                      <input
                        type="text"
                        value={item.icon}
                        onChange={(e) => handleUpdateItem('expertise', index, 'icon', e.target.value)}
                        placeholder="faCode"
                      />
                    </div>
                    <div className="form-group">
                      <label>Title:</label>
                      <input
                        type="text"
                        value={item.title}
                        onChange={(e) => handleUpdateItem('expertise', index, 'title', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Description:</label>
                      <textarea
                        value={item.description}
                        onChange={(e) => handleUpdateItem('expertise', index, 'description', e.target.value)}
                        rows={3}
                      />
                    </div>
                    <div className="form-group">
                      <label>Tech Stack (comma-separated):</label>
                      <textarea
                        value={item.techStack?.join(', ')}
                        onChange={(e) => handleUpdateItem('expertise', index, 'techStack', e.target.value.split(',').map(t => t.trim()))}
                        rows={2}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === 'projects' && (
            <div className="section-editor">
              <div className="section-header">
                <h2>Projects</h2>
                <button className="btn-add" onClick={() => handleAddItem('projects')}>+ Add Item</button>
              </div>
              <div className="items-list">
                {content.projects?.map((item: any, index: number) => (
                  <div key={index} className="item-editor">
                    <div className="item-header">
                      <h3>{item.title}</h3>
                      <button 
                        className="btn-delete" 
                        onClick={() => handleDeleteItem('projects', index)}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                    <div className="form-group">
                      <label>Image URL:</label>
                      <input
                        type="text"
                        value={item.image}
                        onChange={(e) => handleUpdateItem('projects', index, 'image', e.target.value)}
                        placeholder="/image.png"
                      />
                    </div>
                    <div className="form-group">
                      <label>Title:</label>
                      <input
                        type="text"
                        value={item.title}
                        onChange={(e) => handleUpdateItem('projects', index, 'title', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Description:</label>
                      <textarea
                        value={item.description}
                        onChange={(e) => handleUpdateItem('projects', index, 'description', e.target.value)}
                        rows={3}
                      />
                    </div>
                    <div className="form-group">
                      <label>Link:</label>
                      <input
                        type="text"
                        value={item.link}
                        onChange={(e) => handleUpdateItem('projects', index, 'link', e.target.value)}
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === 'aiWork' && (
            <div className="section-editor">
              <div className="section-header">
                <h2>AI Work</h2>
                <button className="btn-add" onClick={() => handleAddItem('aiWork')}>+ Add Item</button>
              </div>
              <div className="items-list">
                {content.aiWork?.map((item: any, index: number) => (
                  <div key={index} className="item-editor">
                    <div className="item-header">
                      <h3>{item.title}</h3>
                      <button 
                        className="btn-delete" 
                        onClick={() => handleDeleteItem('aiWork', index)}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                    <div className="form-group">
                      <label>Title:</label>
                      <input
                        type="text"
                        value={item.title}
                        onChange={(e) => handleUpdateItem('aiWork', index, 'title', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Description:</label>
                      <textarea
                        value={item.description}
                        onChange={(e) => handleUpdateItem('aiWork', index, 'description', e.target.value)}
                        rows={3}
                      />
                    </div>
                    <div className="form-group">
                      <label>Status:</label>
                      <select
                        value={item.status}
                        onChange={(e) => handleUpdateItem('aiWork', index, 'status', e.target.value)}
                      >
                        <option>In Progress</option>
                        <option>Completed</option>
                        <option>Planning</option>
                      </select>
                    </div>
                    <div className="form-group">
                      <label>Technologies (comma-separated):</label>
                      <textarea
                        value={item.technologies?.join(', ')}
                        onChange={(e) => handleUpdateItem('aiWork', index, 'technologies', e.target.value.split(',').map(t => t.trim()))}
                        rows={2}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === 'educationJourney' && (
            <div className="section-editor">
              <div className="section-header">
                <h2>Education Journey</h2>
                <button className="btn-add" onClick={() => handleAddItem('educationJourney')}>+ Add Item</button>
              </div>
              <div className="items-list">
                {content.educationJourney?.map((item: any, index: number) => (
                  <div key={index} className="item-editor">
                    <div className="item-header">
                      <h3>{item.degree}</h3>
                      <button 
                        className="btn-delete" 
                        onClick={() => handleDeleteItem('educationJourney', index)}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                    <div className="form-group">
                      <label>Institution:</label>
                      <input
                        type="text"
                        value={item.institution}
                        onChange={(e) => handleUpdateItem('educationJourney', index, 'institution', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Degree:</label>
                      <input
                        type="text"
                        value={item.degree}
                        onChange={(e) => handleUpdateItem('educationJourney', index, 'degree', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Field:</label>
                      <input
                        type="text"
                        value={item.field}
                        onChange={(e) => handleUpdateItem('educationJourney', index, 'field', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Year:</label>
                      <input
                        type="text"
                        value={item.year}
                        onChange={(e) => handleUpdateItem('educationJourney', index, 'year', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Details:</label>
                      <textarea
                        value={item.details}
                        onChange={(e) => handleUpdateItem('educationJourney', index, 'details', e.target.value)}
                        rows={3}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === 'leadership' && (
            <div className="section-editor">
              <div className="section-header">
                <h2>Leadership</h2>
                <button className="btn-add" onClick={() => handleAddItem('leadership')}>+ Add Item</button>
              </div>
              <div className="items-list">
                {content.leadership?.map((item: any, index: number) => (
                  <div key={index} className="item-editor">
                    <div className="item-header">
                      <h3>{item.title}</h3>
                      <button 
                        className="btn-delete" 
                        onClick={() => handleDeleteItem('leadership', index)}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                    <div className="form-group">
                      <label>Title:</label>
                      <input
                        type="text"
                        value={item.title}
                        onChange={(e) => handleUpdateItem('leadership', index, 'title', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Description:</label>
                      <textarea
                        value={item.description}
                        onChange={(e) => handleUpdateItem('leadership', index, 'description', e.target.value)}
                        rows={3}
                      />
                    </div>
                    <div className="form-group">
                      <label>Impact (comma-separated):</label>
                      <textarea
                        value={item.impact?.join(', ')}
                        onChange={(e) => handleUpdateItem('leadership', index, 'impact', e.target.value.split(',').map(t => t.trim()))}
                        rows={2}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === 'certifications' && (
            <div className="section-editor">
              <div className="section-header">
                <h2>Certifications</h2>
                <button className="btn-add" onClick={() => handleAddItem('certifications')}>+ Add Item</button>
              </div>
              <div className="items-list">
                {content.certifications?.map((item: any, index: number) => (
                  <div key={index} className="item-editor">
                    <div className="item-header">
                      <h3>{item.title}</h3>
                      <button 
                        className="btn-delete" 
                        onClick={() => handleDeleteItem('certifications', index)}
                      >
                        🗑️ Delete
                      </button>
                    </div>
                    <div className="form-group">
                      <label>Title:</label>
                      <input
                        type="text"
                        value={item.title}
                        onChange={(e) => handleUpdateItem('certifications', index, 'title', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Issuer:</label>
                      <input
                        type="text"
                        value={item.issuer}
                        onChange={(e) => handleUpdateItem('certifications', index, 'issuer', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Date:</label>
                      <input
                        type="text"
                        value={item.date}
                        onChange={(e) => handleUpdateItem('certifications', index, 'date', e.target.value)}
                      />
                    </div>
                    <div className="form-group">
                      <label>Credential URL:</label>
                      <input
                        type="text"
                        value={item.credential}
                        onChange={(e) => handleUpdateItem('certifications', index, 'credential', e.target.value)}
                        placeholder="https://..."
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === 'footer' && (
            <div className="section-editor">
              <h2>Footer Section</h2>
              <div className="form-group">
                <label>Message:</label>
                <textarea
                  value={content.footer.message}
                  onChange={(e) => handleUpdateNestedField('footer', 'message', e.target.value)}
                  placeholder="Footer message"
                  rows={4}
                />
              </div>
              <div className="form-group">
                <label>Social Links (JSON format):</label>
                <textarea
                  value={JSON.stringify(content.footer.socialLinks, null, 2)}
                  onChange={(e) => {
                    try {
                      handleUpdateNestedField('footer', 'socialLinks', JSON.parse(e.target.value));
                    } catch (err) {
                      // Invalid JSON - do nothing
                    }
                  }}
                  rows={6}
                  placeholder='[{"platform":"GitHub","url":"https://github.com/..."}]'
                />
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="admin-footer">
        <p>💾 Auto-save enabled | Press CTRL+SHIFT+A to close</p>
      </div>
    </div>
  );
}

export default Admin;